import { createContext, useContext } from "react";
import type { ApiClient } from "./api/client";
import type { BasemapConfig } from "./map/basemap";
import type { FireRouteId } from "./module";
import type { Principal } from "./types";

/** A record another screen should open when it is shown (e.g. "Open the incident" from an alert). */
export interface Focus {
  alertId?: string;
  incidentId?: string;
  premisesId?: string;
  applicationId?: string;
  certificateId?: string;
  hydrantId?: string;
  stationId?: string;
}

export interface FireContextValue {
  api: ApiClient;
  /** The session in use: the shell's principal, or the FER local session's. */
  principal: Principal;
  /** Basemap: a MapLibre style URL (Hub gis tiles) and/or the console's offline PMTiles file; a grid otherwise. */
  basemap: BasemapConfig;
  /** "Now" for clocks, Lusaka days and date ranges (tests pin it). */
  now: () => Date;
  /** Go to another screen of this module, optionally opening a record there. */
  go: (route: FireRouteId, focus?: Focus) => void;
  /** What the current screen was asked to open (consumed by the screen). */
  focus: Focus;
  /** Poll interval of the live map, the queue and the board in ms (0 = off; tests turn it off). */
  refreshMs: number;
  /** Tick of the live clocks in ms (0 = no ticking; tests turn it off). */
  clockTickMs: number;
}

export const FireContext = createContext<FireContextValue | null>(null);

export function useFireContext(): FireContextValue {
  const ctx = useContext(FireContext);
  if (!ctx) throw new Error("useFireContext must be used inside <FireApp>");
  return ctx;
}
